import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useMutation } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { getCoord } from '../../../../services/shared';
import { register, requestOTP } from '../../../../services/auth';
import { COUNTRIES } from '../../../../constants/countries';
import { PLACEHOLDER_PROFILE_BASE64 } from '../../../../constants/placeholderImage';

// Default coordinates by country (fallback when geocoding fails)
const COUNTRY_DEFAULTS = {
  MY: { lat: 3.1390, lng: 101.6869 },   // Kuala Lumpur
  SG: { lat: 1.3521, lng: 103.8198 },   // Singapore
  PH: { lat: 14.5995, lng: 120.9842 },  // Manila
  ID: { lat: -6.2088, lng: 106.8456 },  // Jakarta
  IN: { lat: 28.6139, lng: 77.2090 },   // New Delhi
  VN: { lat: 21.0285, lng: 105.8542 },  // Hanoi
  KH: { lat: 11.5564, lng: 104.9282 },  // Phnom Penh
  TH: { lat: 13.7563, lng: 100.5018 },  // Bangkok
  LA: { lat: 17.9757, lng: 102.6331 },  // Vientiane
};

const DEFAULT_ZOOM = 17;


const StepLocation = ({ formData, updateFormData, goToStep }) => {
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [isGeocoding, setIsGeocoding] = useState(true);
  const [geocodeError, setGeocodeError] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showGuideModal, setShowGuideModal] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // Register mutation - called before requestOTP
  const registerMutation = useMutation(
    (data) => register(data),
    {
      onError: (err) => {
        setIsSubmitting(false);
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setSubmitError(messages.join('\n'));
        } else {
          setSubmitError(data?.message || 'Registration failed');
        }
      },
      onSuccess: () => {
        // Registration successful, now request OTP
        requestOTPMutation.mutate({ email: formData.email });
      },
    }
  );

  // Request OTP mutation - called after register
  const requestOTPMutation = useMutation(
    (data) => requestOTP(data),
    {
      onError: (err) => {
        setIsSubmitting(false);
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setSubmitError(messages.join('\n'));
        } else {
          setSubmitError(data?.message || 'Failed to send verification code');
        }
      },
      onSuccess: (res) => {
        setIsSubmitting(false);
        updateFormData('nonce', res.data.data.nonce);
        goToStep(5); // Go to Verify step
      },
    }
  );

  // Build full address string from form data (using FacilityAuth field names)
  const buildAddressString = useCallback(() => {
    const countryName = COUNTRIES[formData.country]?.name || 'Malaysia';
    const parts = [
      formData.facilityAddress,
      formData.city,
      formData.postalCode,
      formData.state,
      countryName,
    ].filter(Boolean);
    return parts.join(', ');
  }, [formData.facilityAddress, formData.city, formData.postalCode, formData.state, formData.country]);

  // Get default coordinates for country
  const getDefaultCoords = useCallback(() => {
    return COUNTRY_DEFAULTS[formData.country] || COUNTRY_DEFAULTS.MY;
  }, [formData.country]);

  // Geocode the address on mount
  useEffect(() => {
    let isCancelled = false;

    const geocodeAddress = async () => {
      setIsGeocoding(true);
      setGeocodeError(null);

      try {
        const addressString = buildAddressString();
        const response = await getCoord(addressString);

        if (isCancelled) return;

        const coords = response.data?.data;

        if (coords?.latitude && coords?.longitude) {
          const lat = parseFloat(coords.latitude);
          const lng = parseFloat(coords.longitude);

          if (!isNaN(lat) && !isNaN(lng)) {
            const center = { lat, lng };
            setMapCenter(center);
            setSelectedLocation({
              latitude: lat,
              longitude: lng,
            });
          } else {
            throw new Error('Invalid coordinates received');
          }
        } else {
          const defaultCoords = getDefaultCoords();
          setMapCenter(defaultCoords);
          setSelectedLocation({
            latitude: defaultCoords.lat,
            longitude: defaultCoords.lng,
          });
          setGeocodeError('Could not find exact address. Please adjust the pin to your facility location.');
        }
      } catch (error) {
        if (isCancelled) return;

        console.error('Geocoding error:', error);
        const defaultCoords = getDefaultCoords();
        setMapCenter(defaultCoords);
        setSelectedLocation({
          latitude: defaultCoords.lat,
          longitude: defaultCoords.lng,
        });
        setGeocodeError('Could not geocode address. Please position the pin manually on your facility location.');
      } finally {
        if (!isCancelled) {
          setIsGeocoding(false);
        }
      }
    };

    geocodeAddress();

    return () => {
      isCancelled = true;
    };
  }, [buildAddressString, getDefaultCoords]);

  // Initialize Google Map once geocoding is complete
  useEffect(() => {
    if (!mapCenter || !mapContainerRef.current || isGeocoding) return;

    let isMounted = true;

    if (!window.google || !window.google.maps) {
      setGeocodeError('Google Maps could not be loaded. You can continue with the geocoded location, or refresh the page to try again.');
      setIsMapLoading(false);
      return;
    }

    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: mapCenter,
      zoom: DEFAULT_ZOOM,
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      gestureHandling: 'greedy',
    });

    mapRef.current = map;

    map.addListener('center_changed', () => {
      if (!isMounted) return;

      const center = map.getCenter();
      if (center) {
        const lat = center.lat();
        const lng = center.lng();

        if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
          setSelectedLocation({
            latitude: lat,
            longitude: lng,
          });
        }
      }
    });

    setIsMapLoading(false);

    return () => {
      isMounted = false;
      if (mapRef.current) {
        window.google.maps.event.clearInstanceListeners(mapRef.current);
      }
    };
  }, [mapCenter, isGeocoding]);

  // Handle continue button - register user then request OTP
  const handleContinue = () => {
    if (selectedLocation) {
      setIsSubmitting(true);
      setSubmitError('');
      updateFormData('latitude', selectedLocation.latitude);
      updateFormData('longitude', selectedLocation.longitude);

      // Build registration payload
      const registrationPayload = {
        email: formData.email,
        password: formData.password,
        country: formData.country,
        scope: 'user',
        role: 'clinic_owner',
        // Owner info (required by backend)
        last_name: formData.facilityName,
        first_name: '-',
        phone_number: formData.facilityPhone,
        emergency_name: '-',
        emergency_number: formData.facilityPhone,
        education: [],
        // Clinic info
        clinic_name: formData.facilityName,
        business_registration_number: formData.businessRegistrationNumber,
        clinic_phone_number: formData.facilityPhone,
        address: formData.facilityAddress,
        city: formData.city,
        postcode: formData.postalCode,
        state: formData.state,
        type: formData.facilitySubtype,
        facilities: ['doctor-gp-general-practitioner'],
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        // Required image fields
        profile_image: formData.profileImage || PLACEHOLDER_PROFILE_BASE64,
        clinic_profile_image: formData.profileImage || PLACEHOLDER_PROFILE_BASE64,
      };

      // Add optional fields if present
      if (formData.department) {
        registrationPayload.department = formData.department;
      }
      if (formData.fac_id) {
        registrationPayload.fac_id = formData.fac_id;
      }

      registerMutation.mutate(registrationPayload);
    }
  };

  const canContinue = selectedLocation && !isMapLoading && !isGeocoding && !isSubmitting;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.stepHeader}>
        <View style={styles.stepIcon}>
          <MapPin size={48} color={webColors.primary} />
        </View>
        <Text style={styles.stepTitle}>Facility Location</Text>
        <Text style={styles.stepSubtitle}>
          Drag the map to mark your facility's exact location
        </Text>
      </View>

      {/* Geocode Error Warning */}
      {geocodeError && (
        <View style={styles.warningBox}>
          <Text style={styles.warningText}>{geocodeError}</Text>
        </View>
      )}

      {/* Address Display */}
      <View style={styles.addressCard}>
        <View style={styles.addressIconContainer}>
          <Ionicons name="location-outline" size={20} color={webColors.textSecondary} />
        </View>
        <View style={styles.addressTextContainer}>
          <Text style={styles.addressLabel}>Address</Text>
          <Text style={styles.addressText}>{buildAddressString()}</Text>
        </View>
        <TouchableOpacity style={styles.editButton} onPress={() => goToStep(3)}>
          <Text style={styles.editButtonText}>Edit</Text>
        </TouchableOpacity>
      </View>

      {/* Map Container */}
      <View style={styles.mapWrapper}>
        {(isGeocoding || isMapLoading) && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={webColors.primary} />
            <Text style={styles.loadingText}>
              {isGeocoding ? 'Finding your location...' : 'Loading map...'}
            </Text>
          </View>
        )}

        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 12,
          }}
        />

        {!isGeocoding && !isMapLoading && (
          <View style={styles.pinOverlay}>
            <View style={styles.pinContainer}>
              <Ionicons
                name="location-sharp"
                size={55}
                color="#d40b1b"
              />
            </View>
          </View>
        )}
      </View>

      {/* Guide Modal */}
      {showGuideModal && !isGeocoding && !isMapLoading && (
        <View style={styles.guideModal}>
          <View style={styles.guideContent}>
            <View style={styles.guideIconContainer}>
              <Ionicons name="information-circle-outline" size={24} color={webColors.primary} />
            </View>
            <Text style={styles.guideTitle}>Adjust Pin Location</Text>
            <Text style={styles.guideText}>
              Please drag the map to position the pin exactly on your facility's location. This helps doctors find your clinic accurately.
            </Text>
            <TouchableOpacity
              style={styles.guideButton}
              onPress={() => setShowGuideModal(false)}
            >
              <Text style={styles.guideButtonText}>Got it</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Instruction Box */}
      <View style={styles.instructionBox}>
        <Ionicons name="hand-left-outline" size={18} color={webColors.primary} />
        <Text style={styles.instructionText}>
          Drag the map to position the pin on your facility's exact location
        </Text>
      </View>

      {/* Navigation Buttons */}
      <View style={styles.btnRow}>
        <TouchableOpacity
          style={styles.btnBack}
          onPress={() => goToStep(3)}
        >
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnNext, !canContinue && styles.btnNextDisabled]}
          onPress={handleContinue}
          disabled={!canContinue}
        >
          <Text style={[styles.btnNextText, !canContinue && styles.btnNextTextDisabled]}>
            {isGeocoding ? 'Finding...' : isMapLoading ? 'Loading...' : 'Continue'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  stepHeader: {
    marginBottom: 24,
  },
  stepIcon: {
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: webColors.primary,
  },
  warningBox: {
    backgroundColor: webColors.warningLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 16,
  },
  warningText: {
    color: webColors.warning,
    fontSize: 14,
  },
  addressCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: webColors.backgroundAlt,
    padding: 14,
    borderRadius: webBorderRadius.medium,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: webColors.border,
  },
  addressIconContainer: {
    marginRight: 12,
  },
  addressTextContainer: {
    flex: 1,
  },
  addressLabel: {
    fontSize: 12,
    color: webColors.textMuted,
    marginBottom: 2,
  },
  addressText: {
    fontSize: 14,
    color: webColors.textPrimary,
    lineHeight: 20,
  },
  editButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  editButtonText: {
    color: webColors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  mapWrapper: {
    position: 'relative',
    width: '100%',
    height: 350,
    borderRadius: webBorderRadius.large,
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: 2,
    borderColor: webColors.border,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: webColors.backgroundAlt,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: webColors.textSecondary,
  },
  pinOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -27.5 }, { translateY: -55 }],
    zIndex: 5,
    pointerEvents: 'none',
  },
  pinContainer: {
    // shadowColor: '#000',
    // shadowOffset: { width: 0, height: 4 },
    // shadowOpacity: 0.3,
    // shadowRadius: 6,
  },
  guideModal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 20,
    padding: 20,
  },
  guideContent: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    padding: 24,
    maxWidth: 360,
    width: '100%',
    alignItems: 'center',
    ...webShadows.modal,
  },
  guideIconContainer: {
    marginBottom: 12,
  },
  guideTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
    textAlign: 'center',
  },
  guideText: {
    fontSize: 14,
    color: webColors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  guideButton: {
    backgroundColor: webColors.primary,
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  guideButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  instructionBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: webColors.primaryLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 24,
    gap: 10,
  },
  instructionText: {
    flex: 1,
    fontSize: 14,
    color: webColors.primary,
    lineHeight: 20,
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  btnBack: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: '#374151',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    backgroundColor: webColors.border,
    shadowOpacity: 0,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNextTextDisabled: {
    color: webColors.textMuted,
  },
});

export default StepLocation;
